import React, { useState, useEffect } from 'react';
import {
  FiHome,
  FiBox,
  FiFilePlus,
  FiChevronDown,
  FiChevronUp,
  FiPackage,
  FiSettings,
  FiActivity,
} from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';

export default function Sidebar({ open, setOpen }) {
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [commonOpen, setCommonOpen] = useState(false);
  const [stockBlockingOpen, setStockBlockingOpen] = useState(false);
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [incidentalOpen, setIncidentalOpen] = useState(false);
  const [role, setRole] = useState('');
  const location = useLocation();

  // ✅ Get role from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    const userRole = user?.roles?.[0]?.name || '';
    setRole(userRole);
  }, []);

  // Normalize role for comparison (handles "Branch Worker", "Branch-Worker", "branch_worker", etc.)
  const roleLower = role.toLowerCase().replace(/[\s_-]/g, '');

  // ✅ Collapse other dropdowns when one opens
  const handleToggle = (menu) => {
    if (menu === 'inventory') {
      setInventoryOpen(!inventoryOpen);
      setCommonOpen(false);
      setStockBlockingOpen(false);
      setWithdrawalOpen(false);
    } else if (menu === 'common') {
      setCommonOpen(!commonOpen);
      setInventoryOpen(false);
      setStockBlockingOpen(false);
      setWithdrawalOpen(false);
    } else if (menu === 'stockBlocking') {
      setStockBlockingOpen(!stockBlockingOpen);
      setInventoryOpen(false);
      setCommonOpen(false);
      setWithdrawalOpen(false);
    } else if (menu === 'withdrawal') {
      setWithdrawalOpen(!withdrawalOpen);
      setInventoryOpen(false);
      setCommonOpen(false);
      setStockBlockingOpen(false);
      setIncidentalOpen(false);
    } else if (menu === 'incidental') {
      setIncidentalOpen(!incidentalOpen);
      setInventoryOpen(false);
      setCommonOpen(false);
      setStockBlockingOpen(false);
      setWithdrawalOpen(false);
    }
  };

  // ✅ Determine if a link is active
  const isActive = (path) => location.pathname === path;

  const linkBase =
    'flex items-center gap-2 font-medium transition-colors duration-200';
  const linkActive = 'text-blue-400 font-semibold';
  const linkInactive = 'hover:text-blue-300';

  return (
    <div
      className={`transition-all duration-300 ${open ? 'block' : 'hidden'
        } md:block fixed bg-[#1B0063] dark:bg-blue-900 text-white 
        w-full md:w-60 h-[100vh] overflow-y-auto scrollbar-thin scrollbar-thumb-blue-700 scrollbar-track-blue-900 shadow z-40`}
    >
      {/* Header */}
      <div className="flex justify-center items-center p-4">
        <h1 className="text-2xl font-bold">Estock</h1>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-3">
        {/* ✅ Admin Role */}
        {role === 'Admin' && (
          <>
            {/* Dashboard */}
            <Link
              to="/"
              className={`${linkBase} ${isActive('/') ? linkActive : linkInactive
                }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiHome className="text-xl" />
              Dashboard
            </Link>

            {/* Schedule Auction */}
            {/* <Link
              to="/schedule-auction"
              className={`${linkBase} ${
                isActive('/schedule-auction') ? linkActive : linkInactive
              }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiPackage className="text-xl" />
              Schedule Auction
            </Link> */}

            {/* Inventory */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${inventoryOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('inventory')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Inventory
                </span>
                {inventoryOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {inventoryOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/inventory/whrlist"
                    className={`${linkBase} ${isActive('/inventory/whrlist') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR List
                  </Link>
                  <Link
                    to="/inventory/upload"
                    className={`${linkBase} ${isActive('/inventory/upload') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR Wise Stock List
                  </Link>
                  <Link
                    to="/inventory/warehousestoclist"
                    className={`${linkBase} ${isActive('/inventory/warehousestoclist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Warehouse Stock List
                  </Link>
                  <Link
                    to="/inventory/commoditystocklist"
                    className={`${linkBase} ${isActive('/inventory/commoditystocklist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Commodity Stock List
                  </Link>
                </div>
              )}
            </div>

            {/* Stock Blocking */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${stockBlockingOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('stockBlocking')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Stock Blocking
                </span>
                {stockBlockingOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {stockBlockingOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/stock-blocking/get-all"
                    className={`${linkBase} ${isActive('/stock-blocking/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                  <Link
                    to="/stock-blocking/block-stock"
                    className={`${linkBase} ${isActive('/stock-blocking/block-stock')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Block Stock
                  </Link>
                </div>
              )}
            </div>

            {/* Withdrawal */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${withdrawalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('withdrawal')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Withdrawal
                </span>
                {withdrawalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {withdrawalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/withdrawal/get-all"
                    className={`${linkBase} ${isActive('/withdrawal/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                  <Link
                    to="/withdrawal/create"
                    className={`${linkBase} ${isActive('/withdrawal/create') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Stock Withdrawal
                  </Link>
                </div>
              )}
            </div>

            {/* Masters */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${commonOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('common')}
              >
                <span className="flex items-center gap-2">
                  <FiSettings className="text-xl" />
                  Masters
                </span>
                {commonOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {commonOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/warehouse"
                    className={`${linkBase} ${isActive('/warehouse') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> Warehouse
                  </Link>
                  <Link
                    to="/commodity"
                    className={`${linkBase} ${isActive('/commodity') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiPackage /> Commodity
                  </Link>
                  <Link
                    to="/seasons"
                    className={`${linkBase} ${isActive('/seasons') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Seasons
                  </Link>
                  <Link
                    to="/purposes"
                    className={`${linkBase} ${isActive('/purposes') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Purposes
                  </Link>
                  <Link
                    to="/branch"
                    className={`${linkBase} ${isActive('/branch') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> Branch
                  </Link>
                  <Link
                    to="/city"
                    className={`${linkBase} ${isActive('/city') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> City
                  </Link>
                  <Link
                    to="/particulars"
                    className={`${linkBase} ${isActive('/particulars') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Particulars
                  </Link>
                  <Link
                    to="/msp"
                    className={`${linkBase} ${isActive('/msp') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> MSP
                  </Link>
                  <Link
                    to="/incidental-rates"
                    className={`${linkBase} ${isActive('/incidental-rates') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Incidental Rate
                  </Link>
                  <Link
                    to="/bag-size"
                    className={`${linkBase} ${isActive('/bag-size') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Bag Size
                  </Link>
                </div>
              )}
            </div>
            {/* Incidental */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${incidentalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('incidental')}
              >
                <span className="flex items-center gap-2">
                  <FiPackage className="text-xl" />
                  Incidental
                </span>
                {incidentalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {incidentalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/incidental/store"
                    className={`${linkBase} ${isActive('/incidental/store') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Store Incidental
                  </Link>
                  <Link
                    to="/incidental/reports"
                    className={`${linkBase} ${isActive('/incidental/reports')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiActivity /> Reports
                  </Link>
                  <Link
                    to="/incidental/sla"
                    className={`${linkBase} ${isActive('/incidental/sla')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> SLA
                  </Link>
                </div>
              )}
            </div>

            {/* Logs */}
            <Link
              to="/logs"
              className={`${linkBase} ${isActive('/logs') ? linkActive : linkInactive
                }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiActivity className="text-xl" />
              Audit Logs
            </Link>
          </>
        )}

        {/* ✅ Branch Role - Admin-like view scoped to state */}
        {role === 'Branch' && (
          <>
            {/* Dashboard */}
            <Link
              to="/"
              className={`${linkBase} ${isActive('/') ? linkActive : linkInactive
                }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiHome className="text-xl" />
              Dashboard
            </Link>

            {/* Schedule Auction */}
            <Link
              to="/schedule-auction"
              className={`${linkBase} ${isActive('/schedule-auction') ? linkActive : linkInactive
                }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiPackage className="text-xl" />
              Schedule Auction
            </Link>

            {/* Inventory */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${inventoryOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('inventory')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Inventory
                </span>
                {inventoryOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {inventoryOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/inventory/whrlist"
                    className={`${linkBase} ${isActive('/inventory/whrlist') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR List
                  </Link>
                  <Link
                    to="/inventory/upload"
                    className={`${linkBase} ${isActive('/inventory/upload') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR Wise Stock List
                  </Link>
                  <Link
                    to="/inventory/warehousestoclist"
                    className={`${linkBase} ${isActive('/inventory/warehousestoclist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Warehouse Stock List
                  </Link>
                  <Link
                    to="/inventory/commoditystocklist"
                    className={`${linkBase} ${isActive('/inventory/commoditystocklist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Commodity Stock List
                  </Link>
                </div>
              )}
            </div>

            {/* Stock Blocking */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${stockBlockingOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('stockBlocking')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Stock Blocking
                </span>
                {stockBlockingOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {stockBlockingOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/stock-blocking/get-all"
                    className={`${linkBase} ${isActive('/stock-blocking/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                  <Link
                    to="/stock-blocking/block-stock"
                    className={`${linkBase} ${isActive('/stock-blocking/block-stock')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Block Stock
                  </Link>
                </div>
              )}
            </div>

            {/* Withdrawal */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${withdrawalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('withdrawal')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Withdrawal
                </span>
                {withdrawalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {withdrawalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/withdrawal/get-all"
                    className={`${linkBase} ${isActive('/withdrawal/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                  <Link
                    to="/withdrawal/create"
                    className={`${linkBase} ${isActive('/withdrawal/create') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Stock Withdrawal
                  </Link>
                </div>
              )}
            </div>

            {/* Masters - Limited to Warehouse & Commodity only */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${commonOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('common')}
              >
                <span className="flex items-center gap-2">
                  <FiSettings className="text-xl" />
                  Masters
                </span>
                {commonOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {commonOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/warehouse"
                    className={`${linkBase} ${isActive('/warehouse') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> Warehouse
                  </Link>
                  <Link
                    to="/commodity"
                    className={`${linkBase} ${isActive('/commodity') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiPackage /> Commodity
                  </Link>
                  <Link
                    to="/particulars"
                    className={`${linkBase} ${isActive('/particulars') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Particulars
                  </Link>
                  <Link
                    to="/msp"
                    className={`${linkBase} ${isActive('/msp') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> MSP
                  </Link>
                  <Link
                    to="/incidental-rates"
                    className={`${linkBase} ${isActive('/incidental-rates') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Incidental Rate
                  </Link>
                  <Link
                    to="/bag-size"
                    className={`${linkBase} ${isActive('/bag-size') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Bag Size
                  </Link>
                </div>
              )}
            </div>

            {/* Incidental */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${incidentalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('incidental')}
              >
                <span className="flex items-center gap-2">
                  <FiPackage className="text-xl" />
                  Incidental
                </span>
                {incidentalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {incidentalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/incidental/store"
                    className={`${linkBase} ${isActive('/incidental/store') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Store Incidental
                  </Link>
                  <Link
                    to="/incidental/reports"
                    className={`${linkBase} ${isActive('/incidental/reports')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiActivity /> Reports
                  </Link>
                  <Link
                    to="/incidental/sla"
                    className={`${linkBase} ${isActive('/incidental/sla')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> SLA
                  </Link>
                </div>
              )}
            </div>
          </>
        )}

        {/* ✅ Business Role - Everything except Audit Logs */}
        {role === 'Business' && (
          <>
            {/* Dashboard */}
            <Link
              to="/"
              className={`${linkBase} ${isActive('/') ? linkActive : linkInactive
                }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiHome className="text-xl" />
              Dashboard
            </Link>

            {/* Schedule Auction */}
            <Link
              to="/schedule-auction"
              className={`${linkBase} ${isActive('/schedule-auction') ? linkActive : linkInactive
                }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiPackage className="text-xl" />
              Schedule Auction
            </Link>

            {/* Inventory */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${inventoryOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('inventory')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Inventory
                </span>
                {inventoryOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {inventoryOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/inventory/whrlist"
                    className={`${linkBase} ${isActive('/inventory/whrlist') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR List
                  </Link>
                  <Link
                    to="/inventory/upload"
                    className={`${linkBase} ${isActive('/inventory/upload') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR Wise Stock List
                  </Link>
                  <Link
                    to="/inventory/warehousestoclist"
                    className={`${linkBase} ${isActive('/inventory/warehousestoclist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Warehouse Stock List
                  </Link>
                  <Link
                    to="/inventory/commoditystocklist"
                    className={`${linkBase} ${isActive('/inventory/commoditystocklist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Commodity Stock List
                  </Link>
                </div>
              )}
            </div>

            {/* Stock Blocking */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${stockBlockingOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('stockBlocking')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Stock Blocking
                </span>
                {stockBlockingOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {stockBlockingOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/stock-blocking/get-all"
                    className={`${linkBase} ${isActive('/stock-blocking/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                  <Link
                    to="/stock-blocking/block-stock"
                    className={`${linkBase} ${isActive('/stock-blocking/block-stock')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Block Stock
                  </Link>
                </div>
              )}
            </div>

            {/* Withdrawal */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${withdrawalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('withdrawal')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Withdrawal
                </span>
                {withdrawalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {withdrawalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/withdrawal/get-all"
                    className={`${linkBase} ${isActive('/withdrawal/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                  <Link
                    to="/withdrawal/create"
                    className={`${linkBase} ${isActive('/withdrawal/create') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Stock Withdrawal
                  </Link>
                </div>
              )}
            </div>

            {/* Masters */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${commonOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('common')}
              >
                <span className="flex items-center gap-2">
                  <FiSettings className="text-xl" />
                  Masters
                </span>
                {commonOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {commonOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/warehouse"
                    className={`${linkBase} ${isActive('/warehouse') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> Warehouse
                  </Link>
                  <Link
                    to="/commodity"
                    className={`${linkBase} ${isActive('/commodity') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiPackage /> Commodity
                  </Link>
                  <Link
                    to="/seasons"
                    className={`${linkBase} ${isActive('/seasons') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Seasons
                  </Link>
                  <Link
                    to="/purposes"
                    className={`${linkBase} ${isActive('/purposes') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Purposes
                  </Link>
                  <Link
                    to="/branch"
                    className={`${linkBase} ${isActive('/branch') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> Branch
                  </Link>
                  <Link
                    to="/city"
                    className={`${linkBase} ${isActive('/city') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> City
                  </Link>
                  <Link
                    to="/particulars"
                    className={`${linkBase} ${isActive('/particulars') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Particulars
                  </Link>
                  <Link
                    to="/msp"
                    className={`${linkBase} ${isActive('/msp') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> MSP
                  </Link>
                  <Link
                    to="/incidental-rates"
                    className={`${linkBase} ${isActive('/incidental-rates') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Incidental Rate
                  </Link>
                  <Link
                    to="/bag-size"
                    className={`${linkBase} ${isActive('/bag-size') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Bag Size
                  </Link>
                </div>
              )}
            </div>

            {/* Incidental */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${incidentalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('incidental')}
              >
                <span className="flex items-center gap-2">
                  <FiPackage className="text-xl" />
                  Incidental
                </span>
                {incidentalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {incidentalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/incidental/store"
                    className={`${linkBase} ${isActive('/incidental/store') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Store Incidental
                  </Link>
                  <Link
                    to="/incidental/reports"
                    className={`${linkBase} ${isActive('/incidental/reports')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiActivity /> Reports
                  </Link>
                  <Link
                    to="/incidental/sla"
                    className={`${linkBase} ${isActive('/incidental/sla')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> SLA
                  </Link>
                </div>
              )}
            </div>
          </>
        )}

        {/* ✅ User Role - Read-only view */}
        {role === 'User' && (
          <>
            {/* Dashboard */}
            <Link
              to="/"
              className={`${linkBase} ${isActive('/') ? linkActive : linkInactive
                }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiHome className="text-xl" />
              Dashboard
            </Link>

            {/* Schedule Auction */}
            <Link
              to="/schedule-auction"
              className={`${linkBase} ${isActive('/schedule-auction') ? linkActive : linkInactive
                }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiPackage className="text-xl" />
              Schedule Auction
            </Link>

            {/* Inventory */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${inventoryOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('inventory')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Inventory
                </span>
                {inventoryOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {inventoryOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/inventory/whrlist"
                    className={`${linkBase} ${isActive('/inventory/whrlist') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR List
                  </Link>
                  <Link
                    to="/inventory/upload"
                    className={`${linkBase} ${isActive('/inventory/upload') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR Wise Stock List
                  </Link>
                  <Link
                    to="/inventory/warehousestoclist"
                    className={`${linkBase} ${isActive('/inventory/warehousestoclist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Warehouse Stock List
                  </Link>
                  <Link
                    to="/inventory/commoditystocklist"
                    className={`${linkBase} ${isActive('/inventory/commoditystocklist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Commodity Stock List
                  </Link>
                </div>
              )}
            </div>

            {/* Stock Blocking - Get All only */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${stockBlockingOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('stockBlocking')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Stock Blocking
                </span>
                {stockBlockingOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {stockBlockingOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/stock-blocking/get-all"
                    className={`${linkBase} ${isActive('/stock-blocking/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                </div>
              )}
            </div>

            {/* Withdrawal - Get All only */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${withdrawalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('withdrawal')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Withdrawal
                </span>
                {withdrawalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {withdrawalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/withdrawal/get-all"
                    className={`${linkBase} ${isActive('/withdrawal/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                </div>
              )}
            </div>

            {/* Masters */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${commonOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('common')}
              >
                <span className="flex items-center gap-2">
                  <FiSettings className="text-xl" />
                  Masters
                </span>
                {commonOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {commonOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/warehouse"
                    className={`${linkBase} ${isActive('/warehouse') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> Warehouse
                  </Link>
                  <Link
                    to="/commodity"
                    className={`${linkBase} ${isActive('/commodity') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiPackage /> Commodity
                  </Link>
                  <Link
                    to="/seasons"
                    className={`${linkBase} ${isActive('/seasons') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Seasons
                  </Link>
                  <Link
                    to="/purposes"
                    className={`${linkBase} ${isActive('/purposes') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Purposes
                  </Link>
                  <Link
                    to="/branch"
                    className={`${linkBase} ${isActive('/branch') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> Branch
                  </Link>
                  <Link
                    to="/city"
                    className={`${linkBase} ${isActive('/city') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> City
                  </Link>
                  <Link
                    to="/bag-size"
                    className={`${linkBase} ${isActive('/bag-size') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Bag Size
                  </Link>
                </div>
              )}
            </div>

            {/* Incidental */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${incidentalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('incidental')}
              >
                <span className="flex items-center gap-2">
                  <FiPackage className="text-xl" />
                  Incidental
                </span>
                {incidentalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {incidentalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/incidental/store"
                    className={`${linkBase} ${isActive('/incidental/store') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Store Incidental
                  </Link>
                  <Link
                    to="/incidental/reports"
                    className={`${linkBase} ${isActive('/incidental/reports')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiActivity /> Reports
                  </Link>
                  <Link
                    to="/incidental/sla"
                    className={`${linkBase} ${isActive('/incidental/sla')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> SLA
                  </Link>
                </div>
              )}
            </div>
          </>
        )}

        {/* ✅ Branch-Worker Role */}
        {roleLower === 'branchworker' && (
          <>
            {/* Dashboard */}
            <Link
              to="/"
              className={`${linkBase} ${isActive('/') ? linkActive : linkInactive
                }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiHome className="text-xl" />
              Dashboard
            </Link>

            {/* Inventory */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${inventoryOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('inventory')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Inventory
                </span>
                {inventoryOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {inventoryOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/inventory/whrlist"
                    className={`${linkBase} ${isActive('/inventory/whrlist') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR List
                  </Link>
                  <Link
                    to="/inventory/upload"
                    className={`${linkBase} ${isActive('/inventory/upload') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR Wise Stock List
                  </Link>
                  <Link
                    to="/inventory/warehousestoclist"
                    className={`${linkBase} ${isActive('/inventory/warehousestoclist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Warehouse Stock List
                  </Link>
                  <Link
                    to="/inventory/commoditystocklist"
                    className={`${linkBase} ${isActive('/inventory/commoditystocklist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Commodity Stock List
                  </Link>
                </div>
              )}
            </div>

            {/* Stock Blocking - Get All only */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${stockBlockingOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('stockBlocking')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Stock Blocking
                </span>
                {stockBlockingOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {stockBlockingOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/stock-blocking/get-all"
                    className={`${linkBase} ${isActive('/stock-blocking/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                </div>
              )}
            </div>

            {/* Withdrawal - Get All only */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${withdrawalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('withdrawal')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Withdrawal
                </span>
                {withdrawalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {withdrawalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/withdrawal/get-all"
                    className={`${linkBase} ${isActive('/withdrawal/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                </div>
              )}
            </div>

            {/* Masters - same as Branch */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${commonOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('common')}
              >
                <span className="flex items-center gap-2">
                  <FiSettings className="text-xl" />
                  Masters
                </span>
                {commonOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {commonOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/warehouse"
                    className={`${linkBase} ${isActive('/warehouse') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> Warehouse
                  </Link>
                  <Link
                    to="/commodity"
                    className={`${linkBase} ${isActive('/commodity') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiPackage /> Commodity
                  </Link>
                  <Link
                    to="/particulars"
                    className={`${linkBase} ${isActive('/particulars') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Particulars
                  </Link>
                  <Link
                    to="/msp"
                    className={`${linkBase} ${isActive('/msp') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> MSP
                  </Link>
                  <Link
                    to="/incidental-rates"
                    className={`${linkBase} ${isActive('/incidental-rates') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Incidental Rate
                  </Link>
                  <Link
                    to="/bag-size"
                    className={`${linkBase} ${isActive('/bag-size') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Bag Size
                  </Link>
                </div>
              )}
            </div>

            {/* Incidental */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${incidentalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('incidental')}
              >
                <span className="flex items-center gap-2">
                  <FiPackage className="text-xl" />
                  Incidental
                </span>
                {incidentalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {incidentalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/incidental/store"
                    className={`${linkBase} ${isActive('/incidental/store') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Store Incidental
                  </Link>
                  <Link
                    to="/incidental/reports"
                    className={`${linkBase} ${isActive('/incidental/reports')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiActivity /> Reports
                  </Link>
                  <Link
                    to="/incidental/sla"
                    className={`${linkBase} ${isActive('/incidental/sla')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> SLA
                  </Link>
                </div>
              )}
            </div>
          </>
        )}

        {/* ✅ GM Role - Everything except Audit Logs */}
        {roleLower === 'gm' && (
          <>
            {/* Dashboard */}
            <Link
              to="/"
              className={`${linkBase} ${isActive('/') ? linkActive : linkInactive}`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiHome className="text-xl" />
              Dashboard
            </Link>

            {/* Inventory */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${inventoryOpen ? 'text-blue-400' : 'hover:text-blue-300'}`}
                onClick={() => handleToggle('inventory')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Inventory
                </span>
                {inventoryOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>
              {inventoryOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link to="/inventory/upload" className={`${linkBase} ${isActive('/inventory/upload') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> WHR Stock List
                  </Link>
                  <Link to="/inventory/warehousestoclist" className={`${linkBase} ${isActive('/inventory/warehousestoclist') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Warehouse Stock List
                  </Link>
                  <Link to="/inventory/commoditystocklist" className={`${linkBase} ${isActive('/inventory/commoditystocklist') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Commodity Stock List
                  </Link>
                </div>
              )}
            </div>

            {/* Stock Blocking */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${stockBlockingOpen ? 'text-blue-400' : 'hover:text-blue-300'}`}
                onClick={() => handleToggle('stockBlocking')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Stock Blocking
                </span>
                {stockBlockingOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>
              {stockBlockingOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link to="/stock-blocking/get-all" className={`${linkBase} ${isActive('/stock-blocking/get-all') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Get All
                  </Link>
                  <Link to="/stock-blocking/block-stock" className={`${linkBase} ${isActive('/stock-blocking/block-stock') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Block Stock
                  </Link>
                </div>
              )}
            </div>

            {/* Withdrawal */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${withdrawalOpen ? 'text-blue-400' : 'hover:text-blue-300'}`}
                onClick={() => handleToggle('withdrawal')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Withdrawal
                </span>
                {withdrawalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>
              {withdrawalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link to="/withdrawal/get-all" className={`${linkBase} ${isActive('/withdrawal/get-all') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Get All
                  </Link>
                  <Link to="/withdrawal/create" className={`${linkBase} ${isActive('/withdrawal/create') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Stock Withdrawal
                  </Link>
                </div>
              )}
            </div>

            {/* Masters */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${commonOpen ? 'text-blue-400' : 'hover:text-blue-300'}`}
                onClick={() => handleToggle('common')}
              >
                <span className="flex items-center gap-2">
                  <FiSettings className="text-xl" />
                  Masters
                </span>
                {commonOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>
              {commonOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link to="/warehouse" className={`${linkBase} ${isActive('/warehouse') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiHome /> Warehouse
                  </Link>
                  <Link to="/commodity" className={`${linkBase} ${isActive('/commodity') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiPackage /> Commodity
                  </Link>
                  <Link to="/seasons" className={`${linkBase} ${isActive('/seasons') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Seasons
                  </Link>
                  <Link to="/purposes" className={`${linkBase} ${isActive('/purposes') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Purposes
                  </Link>
                  <Link to="/branch" className={`${linkBase} ${isActive('/branch') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiHome /> Branch
                  </Link>
                  <Link to="/city" className={`${linkBase} ${isActive('/city') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiHome /> City
                  </Link>
                  <Link to="/particulars" className={`${linkBase} ${isActive('/particulars') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Particulars
                  </Link>
                  <Link to="/msp" className={`${linkBase} ${isActive('/msp') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> MSP
                  </Link>
                  <Link to="/incidental-rates" className={`${linkBase} ${isActive('/incidental-rates') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Incidental Rate
                  </Link>
                  <Link to="/bag-size" className={`${linkBase} ${isActive('/bag-size') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Bag Size
                  </Link>
                </div>
              )}
            </div>

            {/* Incidental */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${incidentalOpen ? 'text-blue-400' : 'hover:text-blue-300'}`}
                onClick={() => handleToggle('incidental')}
              >
                <span className="flex items-center gap-2">
                  <FiPackage className="text-xl" />
                  Incidental
                </span>
                {incidentalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>
              {incidentalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link to="/incidental/store" className={`${linkBase} ${isActive('/incidental/store') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> Store Incidental
                  </Link>
                  <Link to="/incidental/reports" className={`${linkBase} ${isActive('/incidental/reports') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiActivity /> Reports
                  </Link>
                  <Link to="/incidental/sla" className={`${linkBase} ${isActive('/incidental/sla') ? linkActive : linkInactive}`} onClick={() => window.innerWidth < 768 && setOpen(false)}>
                    <FiFilePlus /> SLA
                  </Link>
                </div>
              )}
            </div>
          </>
        )}

        {/* ✅ Accounts Role - Same sidebar as Business */}
        {roleLower === 'accounts' && (
          <>
            {/* Dashboard */}
            <Link
              to="/"
              className={`${linkBase} ${isActive('/') ? linkActive : linkInactive
                }`}
              onClick={() => window.innerWidth < 768 && setOpen(false)}
            >
              <FiHome className="text-xl" />
              Dashboard
            </Link>

            {/* Inventory */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${inventoryOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('inventory')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Inventory
                </span>
                {inventoryOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {inventoryOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/inventory/whrlist"
                    className={`${linkBase} ${isActive('/inventory/whrlist') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR List
                  </Link>
                  <Link
                    to="/inventory/upload"
                    className={`${linkBase} ${isActive('/inventory/upload') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> WHR Wise Stock List
                  </Link>
                  <Link
                    to="/inventory/warehousestoclist"
                    className={`${linkBase} ${isActive('/inventory/warehousestoclist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Warehouse Stock List
                  </Link>
                  <Link
                    to="/inventory/commoditystocklist"
                    className={`${linkBase} ${isActive('/inventory/commoditystocklist')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Commodity Stock List
                  </Link>
                </div>
              )}
            </div>

            {/* Stock Blocking */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${stockBlockingOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('stockBlocking')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Stock Blocking
                </span>
                {stockBlockingOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {stockBlockingOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/stock-blocking/get-all"
                    className={`${linkBase} ${isActive('/stock-blocking/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                  <Link
                    to="/stock-blocking/block-stock"
                    className={`${linkBase} ${isActive('/stock-blocking/block-stock')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Block Stock
                  </Link>
                </div>
              )}
            </div>

            {/* Withdrawal */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${withdrawalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('withdrawal')}
              >
                <span className="flex items-center gap-2">
                  <FiBox className="text-xl" />
                  Withdrawal
                </span>
                {withdrawalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {withdrawalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/withdrawal/get-all"
                    className={`${linkBase} ${isActive('/withdrawal/get-all')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Get All
                  </Link>
                  <Link
                    to="/withdrawal/create"
                    className={`${linkBase} ${isActive('/withdrawal/create') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Stock Withdrawal
                  </Link>
                </div>
              )}
            </div>

            {/* Masters */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${commonOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('common')}
              >
                <span className="flex items-center gap-2">
                  <FiSettings className="text-xl" />
                  Masters
                </span>
                {commonOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {commonOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/warehouse"
                    className={`${linkBase} ${isActive('/warehouse') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> Warehouse
                  </Link>
                  <Link
                    to="/commodity"
                    className={`${linkBase} ${isActive('/commodity') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiPackage /> Commodity
                  </Link>
                  <Link
                    to="/seasons"
                    className={`${linkBase} ${isActive('/seasons') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Seasons
                  </Link>
                  <Link
                    to="/purposes"
                    className={`${linkBase} ${isActive('/purposes') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Purposes
                  </Link>
                  <Link
                    to="/branch"
                    className={`${linkBase} ${isActive('/branch') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> Branch
                  </Link>
                  <Link
                    to="/city"
                    className={`${linkBase} ${isActive('/city') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiHome /> City
                  </Link>
                  <Link
                    to="/particulars"
                    className={`${linkBase} ${isActive('/particulars') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Particulars
                  </Link>
                  <Link
                    to="/msp"
                    className={`${linkBase} ${isActive('/msp') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> MSP
                  </Link>
                  <Link
                    to="/incidental-rates"
                    className={`${linkBase} ${isActive('/incidental-rates') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Incidental Rate
                  </Link>
                  <Link
                    to="/bag-size"
                    className={`${linkBase} ${isActive('/bag-size') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Bag Size
                  </Link>
                </div>
              )}
            </div>

            {/* Incidental */}
            <div>
              <button
                className={`w-full flex items-center justify-between font-bold text-medium ${incidentalOpen ? 'text-blue-400' : 'hover:text-blue-300'
                  }`}
                onClick={() => handleToggle('incidental')}
              >
                <span className="flex items-center gap-2">
                  <FiPackage className="text-xl" />
                  Incidental
                </span>
                {incidentalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </button>

              {incidentalOpen && (
                <div className="ml-6 mt-3 space-y-3 text-sm">
                  <Link
                    to="/incidental/store"
                    className={`${linkBase} ${isActive('/incidental/store') ? linkActive : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> Store Incidental
                  </Link>
                  <Link
                    to="/incidental/reports"
                    className={`${linkBase} ${isActive('/incidental/reports')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiActivity /> Reports
                  </Link>
                  <Link
                    to="/incidental/sla"
                    className={`${linkBase} ${isActive('/incidental/sla')
                        ? linkActive
                        : linkInactive
                      }`}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <FiFilePlus /> SLA
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
      </nav>
    </div>
  );
}
